import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { dbService } from '../lib/supabase';
import { clearAllEmailVerificationData } from '../utils/sessionStorage';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isEmailVerified: boolean;
  isAdmin: boolean;
  /**
   * The single admin allowed to invite and remove other admins. Always false for a plain admin.
   * This only decides what the UI offers — every privileged action is re-checked server-side.
   */
  isSuperadmin: boolean;
  /** The signed-in admin's own row status, or null when not an admin. See dbService.getAdminStatus. */
  adminStatus: 'pending' | 'active' | null;
  adminCheckLoading: boolean;
  /** False until the first admin lookup finishes for the current signed-in email. */
  adminStatusResolved: boolean;
  refreshAuth: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [adminStatus, setAdminStatus] = useState<'pending' | 'active' | null>(null);
  const [adminCheckLoading, setAdminCheckLoading] = useState(false);
  const [adminStatusResolved, setAdminStatusResolved] = useState(false);
  /** Avoid re-running admin gate when auth token refreshes for the same email. */
  const lastAdminCheckedEmailRef = useRef<string | null>(null);

  /**
   * Clears every admin-derived flag in one place.
   *
   * isAdmin and isSuperadmin must never disagree — a stale isSuperadmin left behind after a sign-out
   * would show the Admin access tab to whoever signs in next on the same tab. Resetting them
   * together makes that state unrepresentable rather than relying on each call site to remember.
   */
  const clearAdminState = useCallback(() => {
    setIsAdmin(false);
    setIsSuperadmin(false);
    setAdminStatus(null);
    setAdminCheckLoading(false);
    setAdminStatusResolved(true);
    lastAdminCheckedEmailRef.current = null;
  }, []);

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setSession(session);
        const nextUser = session?.user ?? null;
        setUser(nextUser);
        setIsEmailVerified(session?.user?.email_confirmed_at ? true : false);
        if (!nextUser?.email) {
          clearAdminState();
        } else {
          const email = nextUser.email.toLowerCase().trim();
          if (lastAdminCheckedEmailRef.current !== email) {
            setAdminCheckLoading(true);
            setAdminStatusResolved(false);
          }
        }
      } catch (error) {
        console.error('Error getting initial session:', error);
        // Clear any potentially corrupted session data
        setSession(null);
        setUser(null);
        setIsEmailVerified(false);
        clearAdminState();
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        try {
          setSession(session);
          const nextUser = session?.user ?? null;
          setUser(nextUser);
          setIsEmailVerified(session?.user?.email_confirmed_at ? true : false);
          if (!nextUser?.email) {
            clearAdminState();
          }

          // Clean up session storage when user signs out
          if (!session) {
            clearAllEmailVerificationData();
          }
        } catch (error) {
          console.error('Error handling auth state change:', error);
        } finally {
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [clearAdminState]);

  // Check admin status when signed-in email changes (not on every token refresh).
  useEffect(() => {
    const checkAdminStatus = async () => {
      // Session may still be restoring on hard reload; avoid treating as non-admin yet.
      if (loading) {
        return;
      }

      if (!user?.email) {
        clearAdminState();
        return;
      }

      const email = user.email.toLowerCase().trim();
      const emailChanged = lastAdminCheckedEmailRef.current !== email;
      if (emailChanged) {
        setAdminCheckLoading(true);
        setAdminStatusResolved(false);
      }

      try {
        const ok = await dbService.isUserAdmin(email);
        setIsAdmin(!!ok);
        // Only admins can be superadmins or have a pending/active status, so non-admins never pay
        // for these lookups.
        setIsSuperadmin(ok ? await dbService.isSuperadmin(email) : false);
        setAdminStatus(ok ? await dbService.getAdminStatus(email) : null);
        lastAdminCheckedEmailRef.current = email;
      } catch (e) {
        console.error('AuthContext admin check:', e);
        setIsAdmin(false);
        setIsSuperadmin(false);
        setAdminStatus(null);
        lastAdminCheckedEmailRef.current = email;
      } finally {
        setAdminCheckLoading(false);
        setAdminStatusResolved(true);
      }
    };
    void checkAdminStatus();
  }, [user?.email, loading, clearAdminState]);

  const refreshAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      setUser(session?.user ?? null);
      setIsEmailVerified(session?.user?.email_confirmed_at ? true : false);
    } catch (error) {
      console.error('Error refreshing auth:', error);
      throw error;
    }
  };

  const signOut = async () => {
    // 'local' scope clears this device's session without the global server-side revoke that returns 403 "session_not_found" once a session is already
    // expired or gone. A missing session means the user is already logged out, so logout must never be blocked by it.
    const { error } = await supabase.auth.signOut({ scope: 'local' });
  
    if (error && (error as { status?: number }).status !== 403) {
      // Log genuinely unexpected errors, but still proceed to clear local state.
      console.error('Unexpected sign-out error:', error);
    }
  
    clearAllEmailVerificationData();
    setSession(null);
    setUser(null);
    setIsEmailVerified(false);
    clearAdminState();
  };

  const value = {
    user,
    session,
    loading,
    isEmailVerified,
    isAdmin,
    isSuperadmin,
    adminStatus,
    adminCheckLoading,
    adminStatusResolved,
    refreshAuth,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};